import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Copy, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const NumberFormatterTool = () => {
  const [input, setInput] = useState('');
  const [locale, setLocale] = useState('en-US');
  const [formats, setFormats] = useState({});

  const formatNumber = () => {
    const num = parseFloat(input.replace(/,/g, ''));
    if (isNaN(num)) {
      toast.error('Please enter a valid number');
      return;
    }

    const formatter = (options) => {
      try {
        return new Intl.NumberFormat(locale, options).format(num);
      } catch (e) {
        return 'Error';
      }
    };

    setFormats({
      standard: formatter({}),
      currency_usd: formatter({ style: 'currency', currency: 'USD' }),
      currency_eur: formatter({ style: 'currency', currency: 'EUR' }),
      percent: formatter({ style: 'percent', minimumFractionDigits: 2 }),
      compact: formatter({ notation: 'compact', compactDisplay: 'short' }),
      scientific: formatter({ notation: 'scientific' }),
      engineering: formatter({ notation: 'engineering' }),
      decimal_2: formatter({ minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      no_decimals: formatter({ maximumFractionDigits: 0 }),
    });

    toast.success('Formatted!');
  };

  const copyValue = (value) => {
    navigator.clipboard.writeText(value);
    toast.success('Copied!');
  };

  const locales = [
    { value: 'en-US', label: 'English (US)' },
    { value: 'en-GB', label: 'English (UK)' },
    { value: 'de-DE', label: 'German' },
    { value: 'fr-FR', label: 'French' },
    { value: 'es-ES', label: 'Spanish' },
    { value: 'ja-JP', label: 'Japanese' },
    { value: 'zh-CN', label: 'Chinese' },
  ];

  const formatLabels = {
    standard: 'Standard',
    currency_usd: 'USD Currency',
    currency_eur: 'EUR Currency',
    percent: 'Percentage',
    compact: 'Compact',
    scientific: 'Scientific',
    engineering: 'Engineering',
    decimal_2: '2 Decimals',
    no_decimals: 'No Decimals',
  };

  return (
    <div className="space-y-4" data-testid="number-formatter-tool">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold">Number Formatter</h3>
      </div>

      <div className="flex gap-2">
        <Input
          placeholder="Enter a number..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          className="flex-1 font-mono bg-background/50"
          data-testid="input"
        />
      </div>

      <div className="flex flex-wrap gap-2">
        {locales.map((l) => (
          <Button
            key={l.value}
            variant={locale === l.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setLocale(l.value)}
          >
            {l.label}
          </Button>
        ))}
      </div>

      <Button onClick={formatNumber} className="w-full" data-testid="format-btn">
        <RefreshCw className="w-4 h-4 mr-2" />
        Format Number
      </Button>

      {Object.keys(formats).length > 0 && (
        <div className="space-y-2">
          {Object.entries(formats).map(([key, value]) => (
            <div
              key={key}
              className="flex items-center justify-between p-2 rounded bg-background/50 border border-border"
            >
              <div>
                <span className="text-xs text-muted-foreground">{formatLabels[key]}</span>
                <div className="font-mono">{value}</div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => copyValue(value)}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NumberFormatterTool;
